import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * The one video on the plan page: a real 1-on-1 call between a maayot teacher and a learner.
 * It has no sound track, so it always plays muted on a loop. The poster is a still from the
 * clip, so nothing of the video loads until it is on screen.
 */
const SESSION = {
  src: '/videos/feedbacksession.mp4',
  poster: '/images/plan/teacher-session.jpg',
  ratio: '1280 / 720',
  /** Translation key. */
  labelKey: 'plan:session.label',
}

/** Plays while on screen, pauses off it, and stays still for anyone who asked for reduced motion. */
export const SessionClip = () => {
  const { t } = useTranslation('plan')
  const frameRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isNear, setIsNear] = useState(false)

  useEffect(() => {
    const node = frameRef.current
    // jsdom and older Safari have no IntersectionObserver: show the poster and leave it there.
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsNear(true)
      return undefined
    }
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsNear(true)
        const video = videoRef.current
        if (!video || still) return
        if (entry.isIntersecting) video.play().catch(() => undefined)
        else video.pause()
      },
      { rootMargin: '100px 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [isNear])

  return (
    <div className='plan-clip-frame' ref={frameRef} style={{ aspectRatio: SESSION.ratio }}>
      {isNear && (
        <video
          ref={videoRef}
          className='plan-clip-video'
          src={SESSION.src}
          poster={SESSION.poster}
          muted
          loop
          playsInline
          preload='none'
          aria-label={t(SESSION.labelKey)}
        />
      )}
    </div>
  )
}

/**
 * A few seconds of the session clip, on demand, from the call rows of the plan table. It is
 * only a muted 5-second loop, so it is a peek, not something to "watch".
 *
 * - Mouse: shows on hover, hides when the pointer leaves.
 * - Touch: a tap toggles it, a tap anywhere else closes it. Focus does NOT open it there, or the
 *   tap would open it on focus and close it again on click.
 * - Keyboard: shows while the link has visible focus. Escape closes it everywhere.
 * It opens above the link, or below when there is no room above.
 */
export const SessionPeek = () => {
  const { t } = useTranslation('plan')
  const [open, setOpen] = useState(false)
  const [below, setBelow] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const pointerType = useRef('')

  const show = () => {
    const rect = wrapRef.current?.getBoundingClientRect()
    setBelow(Boolean(rect && rect.top < 260))
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const still =
    typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)

  return (
    <span
      className='plan-peek'
      ref={wrapRef}
      onPointerEnter={(e) => {
        pointerType.current = e.pointerType
        if (e.pointerType === 'mouse') show()
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setOpen(false)
      }}
    >
      <button
        type='button'
        className='plan-compare-watch'
        aria-expanded={open}
        onPointerDown={(e) => {
          pointerType.current = e.pointerType
        }}
        onClick={() => {
          // A mouse already opened it on hover; touch, pen and keyboard toggle it here.
          if (pointerType.current === 'mouse') return
          if (open) setOpen(false)
          else show()
        }}
        onFocus={(e) => {
          let keyboard = false
          try {
            keyboard = e.currentTarget.matches(':focus-visible')
          } catch {
            /* older browsers: keyboard users still have Enter */
          }
          if (keyboard) show()
        }}
        onBlur={() => setOpen(false)}
      >
        <span className='plan-compare-watch-icon' aria-hidden='true'>
          <svg width='8' height='10' viewBox='0 0 8 10'>
            <path d='M0 0v10l8-5z' fill='currentColor' />
          </svg>
        </span>
        {t('session.peek')}
      </button>
      {open && (
        <span className={`plan-peek-card${below ? ' plan-peek-card--below' : ''}`}>
          <video
            className='plan-peek-video'
            src={SESSION.src}
            poster={SESSION.poster}
            autoPlay={!still}
            muted
            loop
            playsInline
            aria-label={t(SESSION.labelKey)}
          />
          <span className='plan-peek-caption'>{t('session.peekCaption')}</span>
        </span>
      )}
    </span>
  )
}
